import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { lastValueFrom } from 'rxjs';

import { User } from '../models';
import { jhtAuthEndpoints, meaClaimsNamespace, tokenParams } from '../../../auth.config';
import { PlatformService } from './platform.service';
import { DataUpdateRunner } from '../data/data-update-runner';
import { ActiveUserService } from './active-user.service';
import { StorageService } from './storage/storage.service';
import { AuthTokenResponse, TokenService } from './token.service';

import { CapacitorHttp, HttpOptions, HttpResponse } from '@capacitor/core';
import { environment } from '../../environments/environment';

export interface MeaAuthUsersResponse {
    status: string;
    message: string;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    iosOrAndroid: boolean;
    metadata: Record<string, unknown> = {};

    constructor(
        private http: HttpClient,
        private platformService: PlatformService,
        private dataUpdater: DataUpdateRunner,
        private activeUserService: ActiveUserService,
        private storage: StorageService,
        private tokenService: TokenService
    ) {
        this.iosOrAndroid = this.platformService.isNativeDevice;
    }

    async login(username: string, password: string): Promise<any> {
        tokenParams.username = username;
        tokenParams.password = password;

        const accessData: Record<string, any> = await lastValueFrom(
            this.http.post(encodeURI(`${jhtAuthEndpoints.token}`), tokenParams)
        );

        if (accessData.error) {
            return new Error(accessData.error);
        }

        await this.tokenService.setTokens(accessData as AuthTokenResponse);

        const profile = this.profileFromIdToken(accessData.id_token);
        await this.activeUserService.setUser(profile, accessData);

        if (this.platformService.isNativeDevice) {
            await this.dataUpdater.next({
                userProfile: await this.activeUserService.userProfile(),
            });
        }

        return profile;
    }

    /**
     * Builds the user profile from the id_token claims. `region` is added as a
     * custom claim by the MEA post-login Auth0 Action.
     */
    private profileFromIdToken(idToken: string): User {
        const base64 = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
                .join('')
        );
        const claims = JSON.parse(json);

        return {
            user_id: claims.sub,
            email: claims.email,
            email_verified: claims.email_verified,
            name: claims.name,
            nickname: claims.nickname,
            picture: claims.picture,
            updated_at: claims.updated_at,
            user_metadata: {
                region: claims[`${meaClaimsNamespace}region`],
            },
        } as User;
    }

    async logout() {
        await Promise.all([
            this.activeUserService.removeUser(),
            this.tokenService.clear(),
        ]);

        this.dataUpdater.resetUpdateState();

        window.location.reload();
    }

    async isAuthenticated(): Promise<boolean> {
        try {
            const [expiresAt, userData, hasTokens] = await Promise.all([
                this.storage
                    .get('expires_at')
                    .then((expAt) => (expAt ? +expAt : null)),
                this.activeUserService.userProfile(),
                this.tokenService.hasTokens(),
            ]);

            // Check whether the current time is past the access token's expiry time.
            // Sessions without stored tokens (created before BAPI required them) can't
            // load data, so they are logged out and the user signs in again.
            const authenticated = userData && hasTokens && new Date().getTime() < (expiresAt || 0);
            if (!authenticated && userData) {
                this.logout();
            }
            return !!authenticated;
        } catch (_) {
            return false;
        }
    }

    async getDataFromAuthEndpoint(email: string): Promise<MeaAuthUsersResponse> {
        const options: HttpOptions = {
            url: `${environment.apiUrl}/meaAuthUsers`,
            params: { email },
        };
        const response: HttpResponse = await CapacitorHttp.get(options);

        return response.data;
    }
}
